import { useParams, Link } from "react-router-dom";
import { CategoriesV2 } from "@/components/v2/CategoriesV2";
import { RecentList } from "@/components/RecentList";
import { AboutSection, ABOUT_TEXT, DISCLAIMER_TEXT } from "@/components/AboutSection";
import { PageIntro } from "@/components/Reveal";
import { usePageTitle } from "@/lib/hooks";

export function CategoriesPage() {
  usePageTitle("Categories");
  return (
    <>
      <PageIntro eyebrow="Categories" title={"BROWSE BY\nCATEGORY"} desc="Pick a product family to see every brand that publishes price lists for it." />
      <section className="mx-auto max-w-7xl px-5 pb-32 md:px-8"><CategoriesV2 /></section>
    </>
  );
}

export function RecentPage() {
  usePageTitle("Recently Updated");
  return (
    <>
      <PageIntro eyebrow="Fresh uploads" title={"RECENTLY\nUPDATED"} desc="Newly added and recently replaced price lists from every brand. Items marked NEW were published in the last 14 days." />
      <section className="mx-auto max-w-7xl px-5 pb-32 md:px-8"><RecentList limit={60} /></section>
    </>
  );
}

export function AboutPage() {
  usePageTitle("About");
  return (
    <>
      <PageIntro eyebrow="About" title={"BUILT FOR THE\nELECTRICAL TRADE"} desc={ABOUT_TEXT} />
      <section className="mx-auto max-w-7xl px-5 pb-32 md:px-8"><AboutSection showLink={false} /></section>
    </>
  );
}

const LEGAL = {
  privacy: {
    title: "PRIVACY\nPOLICY",
    body: [
      "PRICEHUB does not require visitors to create an account to search or download price lists.",
      "We record usage statistics, such as which brands are searched and which documents are downloaded, to improve the directory. To prevent abuse and count sponsored-brand views and clicks fairly, the visitor's IP address is stored with some of these events (search logs, sign-ups, featured views and clicks, document reports). This data is not sold or shared.",
      "Price-list alerts: if you follow brands, we store your email address, the brands you chose, the time you gave consent and the time you confirmed. Alerts only start after you tap the confirmation link we email you, and every alert email has a one-tap unsubscribe link.",
      "Document reports: if you report a problem with a price list, we store your report and, only if you provide it, your email address so we can reply.",
      "Stored in your own browser only: recent searches, favourite brands, your grid or list view choice, whether you've seen the intro this visit and your intro sound choice. You can clear them by clearing your browser's site data.",
      "Emails (alert confirmations, alerts and admin notices) are sent through our email delivery provider. PRICEHUB does not currently use AI to process visitor data.",
      "Administrator accounts are protected with hashed passwords and secure session cookies.",
    ],
  },
  terms: {
    title: "TERMS AND\nCONDITIONS",
    body: [
      "PRICEHUB provides access to electrical industry price lists for informational purposes only.",
      "Documents remain the property of their respective owners. You may download them for personal or business reference; redistribution is subject to the original publisher's terms.",
      "PRICEHUB does not sell products and is not party to any transaction between buyers and manufacturers or distributors.",
      "Only documents that are authorised for distribution or otherwise lawfully shared are published.",
    ],
  },
  disclaimer: { title: "DISCLAIMER", body: [DISCLAIMER_TEXT, "Only documents that are authorised for distribution or otherwise lawfully shared are published."] },
};

export function LegalPage() {
  const { page } = useParams();
  const content = LEGAL[page] || LEGAL.disclaimer;
  usePageTitle(content.title.replace("\n", " "));
  return (
    <>
      <PageIntro eyebrow="PriceHub" title={content.title} />
      <section className="mx-auto max-w-3xl px-5 pb-32 md:px-8" data-testid={`legal-${page}`}>
        <div className="space-y-5 text-base leading-relaxed text-ph-ink/85 md:text-lg">
          {content.body.map((p) => <p key={p}>{p}</p>)}
        </div>
        <Link to="/" data-testid="legal-back-home" className="mt-10 inline-block font-semibold text-ph-accent hover:underline">Back to search</Link>
      </section>
    </>
  );
}

export function NotFoundPage() {
  usePageTitle("Not found");
  return (
    <div className="mx-auto max-w-3xl px-5 pb-32 pt-44 text-center">
      <p className="font-display text-[8rem] font-extrabold leading-none text-ph-ink">404</p>
      <Link to="/" className="mt-4 inline-block font-semibold text-ph-accent hover:underline" data-testid="notfound-home">Back to PRICEHUB</Link>
    </div>
  );
}
